import {
    AspectRatio,
    Box,
    Center,
    Flex,
    Grid,
    GridItem,
    Image,
} from '@chakra-ui/react';
import { FC } from 'react';
import PlaceholderImage from '../../../images/logo_becx.png';
import { TaskIdentifiers } from '../../constants/stock';
import { formatDate } from '../../helpers/date';
import { hasTask } from '../../helpers/stock';
import { AppointmentInterface } from '../../types/appointment';
import { StockItemInterface } from '../../types/stock';
import { AppointmentForm } from './AppointmentForm';

type Props = {
    currentStock: StockItemInterface | null;
    existingAppointment: AppointmentInterface | null;
    userLocations: string[];
};

export const AppointmentDetail: FC<Props> = ({
    currentStock,
    userLocations,
    existingAppointment,
}) => {
    if (!currentStock) {
        return null;
    }

    return (
        <>
            <Flex
                borderWidth={1}
                px={4}
                py={4}
                flexDir={{ base: 'column', md: 'row' }}
                gap={{ base: 0, md: 4 }}
                alignItems={'flex-start'}
                mb={4}
            >
                <Box
                    width={{ base: 'full', md: '150px' }}
                    mb={{ base: 4, md: 0 }}
                >
                    {currentStock.imageUrl ? (
                        <Image src={currentStock.imageUrl} />
                    ) : (
                        <AspectRatio ratio={16 / 9} bgColor={'gray.100'}>
                            <Center>
                                <Image src={PlaceholderImage} opacity={0.3} />
                            </Center>
                        </AspectRatio>
                    )}
                </Box>

                <Grid
                    gridTemplateColumns={{ base: '1fr', md: 'auto 1fr' }}
                    columnGap={2}
                    color={'secondary'}
                    flex={1}
                >
                    <GridItem pt={{ base: 2, md: 0 }}>Vlootnummer</GridItem>
                    <GridItem fontWeight={'bold'}>
                        {currentStock.stockNumber}
                    </GridItem>
                    <GridItem pt={{ base: 2, md: 0 }}>Kenteken</GridItem>
                    <GridItem fontWeight={'bold'}>
                        {currentStock.licensePlate}
                    </GridItem>
                    <GridItem pt={{ base: 2, md: 0 }}>Contract</GridItem>
                    <GridItem fontWeight={'bold'}>
                        {currentStock.contractType}
                    </GridItem>
                </Grid>

                <Grid
                    gridTemplateColumns={{ base: '1fr', md: 'auto 1fr' }}
                    columnGap={8}
                    rowGap={{ base: 0, md: 2 }}
                    color={'secondary'}
                    flex={1}
                >
                    <GridItem pt={{ base: 2, md: 0 }}>Type</GridItem>
                    <GridItem fontWeight={'bold'}>
                        {currentStock.type.title}
                    </GridItem>

                    {hasTask(currentStock.type.tasks, [TaskIdentifiers.APK]) &&
                        currentStock.apkDate && (
                            <>
                                <GridItem pt={{ base: 2, md: 0 }}>
                                    APK Datum
                                </GridItem>
                                <GridItem fontWeight={'bold'}>
                                    {formatDate(currentStock.apkDate)}
                                </GridItem>
                            </>
                        )}

                    {hasTask(currentStock.type.tasks, [
                        TaskIdentifiers.TACHOGRAPH,
                    ]) &&
                        currentStock.tachographDate && (
                            <>
                                <GridItem pt={{ base: 2, md: 0 }}>
                                    Verloopdatum Tacho ijken
                                </GridItem>
                                <GridItem fontWeight={'bold'}>
                                    {formatDate(currentStock.tachographDate)}
                                </GridItem>
                            </>
                        )}

                    {hasTask(currentStock.type.tasks, [
                        TaskIdentifiers.EXAMINATION,
                    ]) &&
                        currentStock.examinationDate && (
                            <>
                                <GridItem pt={{ base: 2, md: 0 }}>
                                    Verloopdatum Keuring
                                </GridItem>
                                <GridItem fontWeight={'bold'}>
                                    {formatDate(currentStock.examinationDate)}
                                </GridItem>
                            </>
                        )}

                    {hasTask(currentStock.type.tasks, [
                        TaskIdentifiers.TAILGATE,
                    ]) &&
                        currentStock.tailgateDate && (
                            <>
                                <GridItem pt={{ base: 2, md: 0 }}>
                                    Laadklep keuring
                                </GridItem>
                                <GridItem fontWeight={'bold'}>
                                    {formatDate(currentStock.tailgateDate)}
                                </GridItem>
                            </>
                        )}

                    {hasTask(currentStock.type.tasks, [
                        TaskIdentifiers.CFK_EXAMINATION,
                    ]) &&
                        currentStock.cfkExaminationDate && (
                            <>
                                <GridItem pt={{ base: 2, md: 0 }}>
                                    CFK Keuring
                                </GridItem>
                                <GridItem fontWeight={'bold'}>
                                    {formatDate(
                                        currentStock.cfkExaminationDate
                                    )}
                                </GridItem>
                            </>
                        )}
                </Grid>
            </Flex>

            <AppointmentForm
                stockItem={currentStock}
                existingAppointment={existingAppointment}
                locations={userLocations}
            />
        </>
    );
};
